import { cn } from '@/lib/utils';
import { DECISION_STATUSES, type DecisionStatus } from '@/lib/designDecisions';

/**
 * The state an ADR's header declares — proposed → accepted → superseded /
 * deprecated, or rejected (Documentation Standard §3.2). One axis, distinct
 * from a draft's lifecycle (LifecycleBadge) and from publish state
 * (PublishedBadge). A header that declares no state shows as exactly that,
 * in the same amber LifecycleBadge gives an unknown value — never as a
 * guessed one.
 */
const STYLES: Record<DecisionStatus, string> = {
  proposed: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  accepted: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  draft: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  superseded: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
  deprecated: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};
const NONE = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';

function isDecisionStatus(status: string): status is DecisionStatus {
  return (DECISION_STATUSES as readonly string[]).includes(status);
}

export function DecisionBadge({ status }: { status: string }) {
  const known = isDecisionStatus(status);
  return (
    <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium capitalize', known ? STYLES[status] : NONE)}>
      {known ? status : 'no status'}
    </span>
  );
}
